import { startCase } from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { Add, Close } from '@material-ui/icons';
import { EntityColumnNamePicker } from '@sierralabs/entity-ui';
import { State, WorkflowInvoke } from '@sierralabs/workflow';
import {
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin,
  TaskContext,
  useFindTaskTypeById
} from '@sierralabs/workflow-ui';

import JiraIssueFieldPicker from '../../../jira-connect/pickers/JiraIssueFieldPicker';
import JiraIssueTypePicker from '../../../jira-connect/pickers/JiraIssueTypePicker';
import JiraProjectPicker from '../../../jira-connect/pickers/JiraProjectPicker';

const StyledConfiguratorDiv = styled.div`
  .field {
    margin-top: 12px;
    margin-bottom: 12px;
  }
  .MuiTableHead-root .MuiTableCell-root {
    padding-left: 4px;
    padding-right: 4px;
  }
  .MuiTableRow-root.dense .MuiTableCell-root {
    padding: 2px 4px;
  }
`;

export interface UpdateFieldFromJiraParams {
  src: 'updateFieldFromJira';
  data: {
    projectId?: string;
    fields?: {
      entityField?: string;
      issueType?: { id: string; name: string };
      issueField?: string;
    }[];
  };
}

const UpdateFieldFromJiraConfigurator: React.FC<
  ServiceConfiguratorProps
> = props => {
  const { taskTypeId, onChange } = props;
  const [invoke, setInvoke] = useState(props.invoke);
  if (!invoke.data) invoke.data = {};
  const data: UpdateFieldFromJiraParams['data'] = invoke.data;
  const fields = data.fields || [];
  const { data: taskType, get } = useFindTaskTypeById(taskTypeId || 0);

  const update = (data: UpdateFieldFromJiraParams['data']) => {
    const updatedInvoke = { ...invoke, data };
    setInvoke(updatedInvoke);
    onChange?.(updatedInvoke);
  };

  useEffect(() => {
    get();
  }, [get]);

  return (
    <StyledConfiguratorDiv>
      <JiraProjectPicker
        value={data.projectId}
        textFieldProps={{ size: 'small' }}
        onChange={projectId => {
          data.projectId = projectId;
          data.fields = [];
          update(data);
        }}
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Entity Field</TableCell>
              <TableCell>Issue Type</TableCell>
              <TableCell>Issue Field</TableCell>
              <TableCell style={{ width: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  {data.projectId ? (
                    'No fields added.'
                  ) : (
                    <>
                      Select a <strong>Project</strong> to add fields.
                    </>
                  )}
                </TableCell>
              </TableRow>
            )}
            {fields.map((field, index) => (
              <TableRow key={index} className='dense'>
                <TableCell>
                  <EntityColumnNamePicker
                    value={field.entityField}
                    entityId={taskType?.entityId}
                    entityTable={taskType?.entityTable}
                    textFieldProps={{
                      size: 'small',
                      label: undefined,
                      placeholder: 'Entity Field'
                    }}
                    onChange={columnName => {
                      fields[index].entityField = columnName;
                      update({ ...data, fields });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <JiraIssueTypePicker
                    value={field.issueType?.id}
                    projectId={data.projectId}
                    disabled={!data.projectId}
                    textFieldProps={{
                      size: 'small',
                      label: undefined,
                      placeholder: 'Issue Type'
                    }}
                    onChange={issueType => {
                      fields[index].issueType = issueType && {
                        id: issueType.id,
                        name: issueType.label
                      };
                      fields[index].issueField = undefined;
                      update({ ...data, fields });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <JiraIssueFieldPicker
                    value={field.issueField}
                    projectId={data.projectId || ''}
                    issueTypeId={field.issueType?.id || ''}
                    disabled={!data.projectId || !field.issueType?.id}
                    textFieldProps={{
                      size: 'small',
                      label: undefined,
                      placeholder: 'Issue Field'
                    }}
                    onChange={issueField => {
                      fields[index].issueField = issueField;
                      update({ ...data, fields });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size='small'
                    disabled={!data.projectId}
                    onClick={() => {
                      fields.splice(index, 1);
                      update({ ...data, fields });
                    }}
                  >
                    <Close />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box mt={2}>
        <Button
          variant='outlined'
          startIcon={<Add />}
          disabled={!data.projectId}
          onClick={() => {
            fields.push({});
            update({ ...data, fields });
          }}
        >
          Add Field
        </Button>
      </Box>
    </StyledConfiguratorDiv>
  );
};

const StyledDisplayDiv = styled.div`
  font-size: 13px;
`;

const UpdateFieldFromJiraDisplay: React.FC<ServiceDisplayProps> = props => {
  const { invoke } = props;
  const data: UpdateFieldFromJiraParams['data'] | undefined = invoke.data;
  const fields = data?.fields || [];
  const fieldsText = fields
    .slice(0, 3)
    .map((field, index) => {
      return index === 2
        ? `and ${fields.length - 2} more`
        : startCase(field.entityField) || '?';
    })
    .join(', ');
  return (
    <StyledDisplayDiv>{fieldsText && `Fields: ${fieldsText}`}</StyledDisplayDiv>
  );
};

export const UpdateFieldFromJiraPlugin: ServicePlugin<UpdateFieldFromJiraParams> =
  {
    src: 'updateFieldFromJira',
    name: 'Update Field From Jira',
    configurator: UpdateFieldFromJiraConfigurator,
    display: UpdateFieldFromJiraDisplay,
    validate: (condition: Partial<WorkflowInvoke>) => {
      const data = condition.data as UpdateFieldFromJiraParams['data'];
      return !!(
        data &&
        data.projectId &&
        Array.isArray(data.fields) &&
        data.fields.length > 0 &&
        data.fields.every(
          field => field.entityField && field.issueType?.id && field.issueField
        )
      );
    },
    service: (
      state: State<TaskContext>,
      params: UpdateFieldFromJiraParams | UpdateFieldFromJiraParams[]
    ) => {
      console.log('updateFieldFromJira params', state, params);
      return true;
    }
  };
